import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';

export type FeaturedInsightsType = 'recording' | 'heatmap' | 'feedback' | 'survey';

export type Categories = 'recordings' | 'heatmaps' | 'feedback' | 'surveys';

export type InsightsData = RecordingsData | HeatmapsData | FeedbackData | SurveyData;

export type FeedbackSentiment = 'Love' | 'Like' | 'Neutral' | 'Dislike' | 'Hate';

type ThumbnailType = 'feedback' | 'heatmap_screenshot' | 'heatmap_overlay' | 'recording' | 'none';

export type ThumbnailUrl = string;

export type Thumbnails = {
  id: string;
  thumbnailType: ThumbnailType;
  url: ThumbnailUrl;
}[];

export type ThumbnailDto = {
  id: string;
  image_url?: string;
  insight_id: string | number;
  site_id: number;
  status: 'pending' | 'success' | 'failure';
  type: ThumbnailType;
};

export type Thumbnail = {
  id: string;
  imageUrl?: string;
  insightId: string | number;
  siteId: number;
  status: 'pending' | 'success' | 'failure';
  type: ThumbnailType;
};

/* eslint-disable camelcase */
export interface RecordingInsightsDataDto {
  action_count: number;
  country_code: string;
  created_at: number;
  device: string;
  duration: number;
  exit_page_url: string;
  id: number;
  landing_page_url: string;
  page_count: number;
  public_url: string;
  response_url: string;
  url?: string;
  relevance_score: number;
  thumbnails: Thumbnails | null;
}

export type RecordingsData = {
  actionCount: number;
  countryCode: string;
  createdAt: number;
  device: string;
  duration: number;
  exitPageUrl: string;
  id: number;
  landingPageUrl: string;
  pageCount: number;
  publicUrl?: string;
  responseUrl?: string;
  url?: string;
  relevanceScore: number;
  thumbnails?: Thumbnails | null;
};

export interface FeedbackInsightsDataDto {
  country_code?: string;
  country_name?: string;
  created: number;
  device: string;
  emotion_name: string;
  emotion: EmotionScore;
  feedback_widget_id: number;
  id: number;
  image_url?: string;
  index: number;
  message: string;
  public_url: string;
  response_url: string;
  url?: string;
  short_visitor_uuid: string;
  thumbnails?: Thumbnails;
}

export type FeedbackData = {
  countryCode?: string;
  countryName?: string;
  created: number;
  device: string;
  emotion: EmotionScore;
  emotionName: string;
  feedbackWidgetId: number;
  id: number;
  imageUrl?: string;
  index: number;
  message: string;
  publicUrl: string;
  responseUrl: string;
  url?: string;
  shortVisitorUuid: string;
  thumbnails?: Thumbnails;
};

export interface HeatmapInsightsDataDto {
  average_time: number;
  clicks: number;
  id: string;
  rage_clicks: number;
  sessions: number;
  thumbnails: Thumbnails | null;
  uTurns: number;
}

export type HeatmapsData = {
  averageTime: number;
  clicks: number;
  id: string;
  publicUrl?: string /* <- this one should be created, since it's not included into API response */;
  rageClicks: number;
  responseUrl?: string;
  url?: string;
  sessions: number;
  thumbnails: Thumbnails | null;
  uTurns: number;
};

export interface SurveyInsightsDataDto {
  answers: {
    content: {
      comment: string;
      value: string | number;
    }[];
    question: {
      has_image: boolean;
      id: number;
      reaction_style: string;
      short_uuid: string;
      text: string;
      type: string;
      uuid: string;
    };
  }[];
  country_code?: string;
  country_name?: string;
  created: number;
  device: string;
  id: string;
  index: number;
  name: string;
  public_url: string;
  response_url?: string;
  url?: string;
  short_visitor_uuid: string;
  survey_id: number;
  thumbnails?: Thumbnails;
}

export type SurveyData = {
  answers: {
    content: {
      comment: string;
      value: string | number;
    }[];
    question: {
      hasImage: boolean;
      id: number;
      reactionStyle: string;
      shortUuid: string;
      text: string;
      type: string;
      uuid: string;
    };
  }[];
  countryCode?: string;
  countryName?: string;
  created: number;
  device: string;
  id: string;
  index: number;
  name: string;
  publicUrl?: string;
  responseUrl?: string;
  url?: string;
  shortVisitorUuid: string;
  surveyId: number;
  thumbnails?: Thumbnails;
};

export interface FeaturedInsightsContentDTO {
  insights: {
    details:
      | RecordingInsightsDataDto
      | FeedbackInsightsDataDto
      | HeatmapInsightsDataDto
      | SurveyInsightsDataDto;
    type: FeaturedInsightsType;
  }[];
}

export interface FeaturedInsightsContent {
  insights: {
    details: InsightsData;
    type: FeaturedInsightsType;
  }[];
}

export enum CategoryName {
  recording = 'recordings',
  heatmap = 'heatmaps',
  feedback = 'feedback',
  survey = 'surveys',
}
